// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  mapInstitutionCatalogOptions,
  type InstitutionCatalogOption,
} from '@client/banks/utils/institution-catalog';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { useDebounce } from '@shared/ui-kit/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CountrySelectorOption = {
  value: string;
  label: string;
};

export const useInstitutionCatalog = (selectedListKey?: string) => {
  const { t, i18n } = useTranslation();
  const [country, setCountry] = useState('');
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 300);

  const searchInstitutionsQuery = advisoryApi.useSearchAdvisoryInstitutionsQuery({
    country: country || undefined,
    q: debouncedSearch.trim() || undefined,
  });

  const countryCodes = useMemo(
    () =>
      t('aggregator.countries', {
        ns: 'aggregator',
        returnObjects: true,
      }) as unknown as string[],
    [t],
  );

  const countryOptions = useMemo(() => {
    const displayNames = new Intl.DisplayNames([i18n.resolvedLanguage ?? 'en'], {
      type: 'region',
    });

    return countryCodes
      .map(code => ({
        code,
        label: displayNames.of(code) ?? code,
      }))
      .sort((countryA, countryB) =>
        countryA.label.localeCompare(countryB.label, i18n.resolvedLanguage, {
          sensitivity: 'base',
        }),
      );
  }, [countryCodes, i18n.resolvedLanguage]);

  const selectorCountryOptions = useMemo<CountrySelectorOption[]>(
    () =>
      [
        { value: '', label: t('aggregator.all-countries', { ns: 'aggregator' }) },
      ].concat(
        countryOptions.map(option => ({
          value: option.code,
          label: option.label,
        })),
      ),
    [countryOptions, t],
  );

  const countryByCode = useMemo(
    () =>
      new Map([
        ['ALL', t('aggregator.available-all-countries', { ns: 'aggregator' })],
        ...countryOptions.map(item => [item.code, item.label] as const),
      ]),
    [countryOptions, t],
  );

  const allInstitutionOptions = useMemo<InstitutionCatalogOption[]>(
    () => mapInstitutionCatalogOptions(searchInstitutionsQuery.data),
    [searchInstitutionsQuery.data],
  );

  const institutions = useMemo(
    () =>
      mapInstitutionCatalogOptions(searchInstitutionsQuery.data, selectedListKey),
    [searchInstitutionsQuery.data, selectedListKey],
  );

  const onCountryChange = useCallback((value: string) => {
    setCountry(value);
  }, []);

  const onSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const isLoading =
    searchInstitutionsQuery.isLoading || searchInstitutionsQuery.isFetching;

  return {
    t,
    countryOptions: selectorCountryOptions,
    country,
    search,
    institutions,
    countryByCode,
    isLoading,
    isError: searchInstitutionsQuery.isError,
    onRetry: searchInstitutionsQuery.refetch,
    onCountryChange,
    onSearchChange,
    hasInstitutions: institutions.length > 0,
    allInstitutionOptions,
  };
};
